import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ServiceAgreementPanel from '../components/ServiceAgreementPanel';
import { isMaintenancePreviewEmbed } from '../lib/previewEnvironment';
import { ISSUED_ACCESS_ACTIVATION_ROUTE } from '../lib/issuedAccessActivation';
import { SCREENS } from '../lib/screenRegistry';
import { authApi } from '../services/api';
import './IssuedAccessActivation.css';

const LOGO = encodeURI('/Assets/logo/Mentology Logo.png');

type IssuedKind = 'human_mentor' | 'org_mentee';

type IssuedLookup = {
  ok?: boolean;
  kind?: IssuedKind;
  key?: string;
  displayName?: string;
  contactEmail?: string;
  parentOrgId?: string;
  parentOrgName?: string;
  agreementAccepted?: boolean;
  introCompleted?: boolean;
  error?: string;
};

const PREVIEW: IssuedLookup = {
  ok: true,
  kind: 'human_mentor',
  key: 'ELTM-TEST',
  displayName: 'Preview Human Mentor',
  contactEmail: 'preview@mentology.com.au',
  parentOrgId: 'ELT-DEV-9842',
  parentOrgName: 'Mentology',
  agreementAccepted: false,
};

export default function IssuedAccessActivation() {
  const navigate = useNavigate();
  const preview = isMaintenancePreviewEmbed();
  const [lookup, setLookup] = useState<IssuedLookup>(preview ? PREVIEW : {});
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(!preview);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (preview) return undefined;
    let cancelled = false;
    authApi.getIssuedAccess()
      .then((data: IssuedLookup) => {
        if (cancelled) return;
        setLookup(data);
        if (data.agreementAccepted) {
          navigate(data.kind === 'human_mentor' && data.introCompleted
            ? '/mentor-console'
            : '/introduction', { replace: true });
        }
      })
      .catch((err: { error?: string }) => {
        if (cancelled) return;
        setError(err.error || 'Issued access session required. Enter your key at the Mentoring Portal.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, preview]);

  const handleContinue = async (event: FormEvent) => {
    event.preventDefault();
    if (!agreed || busy) return;
    if (preview) {
      navigate('/introduction', { replace: true });
      return;
    }
    setBusy(true);
    setError('');
    try {
      await authApi.acceptIssuedAccess();
      navigate('/introduction', { replace: true });
    } catch (err: unknown) {
      const record = err as { error?: string };
      setError(record.error || 'Could not record the Service Agreement.');
      setBusy(false);
    }
  };

  const isHuman = lookup.kind !== 'org_mentee';

  return (
    <div
      className="issued-access-screen"
      data-screen-id={SCREENS.ELT13}
      data-screen-route={ISSUED_ACCESS_ACTIVATION_ROUTE}
    >
      <div className="issued-access-logo-wrap">
        <img
          src={LOGO}
          alt="Mentology"
          className="issued-access-logo brand-logo"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </div>

      <div className="issued-access-content">
        <p className="issued-access-kicker">
          {isHuman ? 'Human Mentor activation' : 'Organisation mentee activation'}
        </p>
        <h1 className="section-header issued-access-heading">Issued Access Activation Gate</h1>
        <p className="issued-access-lede">
          Your organisation issued this active key. Accept the Service Agreement to continue to the Executive Introduction. This does not create a new key.
        </p>

        {loading ? (
          <p className="issued-access-lede">Loading issued access details…</p>
        ) : lookup.key ? (
          <dl className="issued-access-card">
            <div>
              <dt>{isHuman ? 'Human Mentor key' : 'Mentee key'}</dt>
              <dd>{lookup.key || '—'}</dd>
            </div>
            <div>
              <dt>Parent organisation</dt>
              <dd>{lookup.parentOrgName || lookup.parentOrgId || '—'}</dd>
            </div>
            <div>
              <dt>Display name</dt>
              <dd>{lookup.displayName || '—'}</dd>
            </div>
            {lookup.contactEmail ? (
              <div>
                <dt>Email</dt>
                <dd>{lookup.contactEmail}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}

        {error ? <p className="error-text issued-access-error">{error}</p> : null}

        {lookup.key || preview ? (
        <form className="issued-access-form" onSubmit={handleContinue}>
          <ServiceAgreementPanel agreed={agreed} onAgreedChange={setAgreed} variant="compact" />
          <div className="issued-access-actions">
            <button type="submit" className="btn-primary" disabled={busy || !agreed || loading}>
              {busy ? 'Activating…' : 'Continue to Executive Introduction'}
            </button>
            <Link to="/portal-login" className="btn-primary">Back to Mentoring Portal</Link>
          </div>
        </form>
        ) : (
          <div className="issued-access-actions">
            <Link to="/portal-login" className="btn-primary">Back to Mentoring Portal</Link>
          </div>
        )}
      </div>
    </div>
  );
}
